import dayjs from "dayjs";

import { AmortizationRow, AmortizationRowComputed, ParametricosProjectionForm } from "../types";

export const roundCreditAmount = (creditSurfaceHa: number, creditQuotaPerHectare: number): number =>
    Math.round(creditSurfaceHa * creditQuotaPerHectare);

export const computeFinancingAmount = (assetValue: number, override: number | null): number =>
    override ?? assetValue * 0.8;

export const computeMaturityDate = (openingDate: string): string =>
    dayjs(openingDate).isValid() ? dayjs(openingDate).add(1080, "day").toISOString() : "";

export const computeCreditTermMonths = (maturityDate: string, evaluationDate: string): number => {
    if (!dayjs(maturityDate).isValid() || !dayjs(evaluationDate).isValid()) return 0;
    return Math.round(dayjs(maturityDate).diff(dayjs(evaluationDate), "day") / 30);
};

/** Tabla de amortización de DATOS: intereses sobre saldo insoluto (primer pago sobre el importe a financiar). */
export const computeAmortizationSchedule = (
    rows: Array<AmortizationRow>,
    tasaContratada: number,
    evaluationDate: string,
    financingAmount: number,
): Array<AmortizationRowComputed> => {
    return rows.map((row, index) => {
        const previousDate = index === 0 ? evaluationDate : rows[index - 1].date;
        const days = Math.max(dayjs(row.date).diff(dayjs(previousDate), "day"), 0);
        const base =
            index === 0 ? financingAmount : rows.slice(index).reduce((sum, current) => sum + current.capital, 0);

        return { ...row, interest: (tasaContratada / 360) * days * base };
    });
};

export const computeLiquidez = (
    productionCostYear1: number,
    creditoAutorizado: number,
    firstAmortizationCapital: number,
    shortTermLiabilities: number,
    year1Amortization: number,
): number => {
    const denominator = creditoAutorizado + firstAmortizationCapital + shortTermLiabilities + year1Amortization;
    return denominator === 0 ? 0 : productionCostYear1 / denominator;
};

export const computeApalancamiento = (
    totalLiabilities: number,
    creditoAutorizado: number,
    financingAmount: number,
    productionCostYear1: number,
    totalFixedAssets: number,
    assetValue: number,
): number => {
    const numerator = totalLiabilities + creditoAutorizado + financingAmount;
    const denominator = productionCostYear1 + totalFixedAssets + assetValue - numerator;
    return denominator === 0 ? 0 : numerator / denominator;
};

/** Semilla inicial de "4.2 b).- Interés de Créd Simple" para una proyección que aún no guardó este campo. */
export const computeDefaultInteresCreditoSimple = (
    longTermAmortizations: [number, number, number, number, number],
    yearsCount: number,
): Array<number> =>
    Array.from({ length: yearsCount }, (_, i) => {
        if (i > 3) return 0;
        let pending = 0;
        for (let k = i; k <= 3; k++) pending += longTermAmortizations[k] ?? 0;
        return pending * 0.2;
    });

/** Semilla inicial de "6.2 b).- Interés" para una proyección que aún no guardó este campo. */
export const computeDefaultInteresCredito = (
    amortizationSchedule: Array<AmortizationRowComputed>,
    yearsCount: number,
): Array<number> => Array.from({ length: yearsCount }, (_, i) => amortizationSchedule[i]?.interest ?? 0);

export interface ProjectionTable {
    ventasNetas: Array<number>;
    ingresosGrano: Array<number>;
    ingresosServicios: Array<number>;
    ingresosApoyo: Array<number>;
    costosTotales: Array<number>;
    costosProduccion: Array<number>;
    costoRenta: Array<number>;
    costosServicios: Array<number>;
    utilidadOperativa: Array<number>;
    pagoObligaciones: Array<number>;
    capitalAmortizacion: Array<number>;
    interesCreditoSimple: Array<number>;
    interesCapitalTrabajo: Array<number>;
    saldo1: Array<number>;
    pagoCreditoProyectado: Array<number>;
    capitalCredito: Array<number>;
    interesCredito: Array<number>;
    interesAvioCt: Array<number>;
    saldo2: Array<number>;
    otrosIngresos: Array<number>;
    otrosGastos: Array<number>;
    utilidadEjercicio: Array<number>;
    capacidadDePago: Array<number | null>;
    plazoCredito: Array<number>;
}

export interface BuildProjectionTableParams {
    hectares: number;
    yieldPerHectare: number;
    pricePerTon: number;
    costPerHectare: number;
    /** Tabla de amortización de DATOS (columna CAPITAL/INTERESES), transpuesta: renglón n -> Año n. */
    amortizationSchedule: Array<AmortizationRowComputed>;
    /** Amortizaciones de créditos vigentes de largo plazo (Año 1..5); solo Años 1-4 fluyen al renglón 4.1. */
    longTermAmortizations: [number, number, number, number, number];
    /** Tasa contratada, como decimal (ej. 0.183 para 18.3%). */
    tasaContratada: number;
    creditoAutorizado: number;
    plazoCreditoMeses: number;
    projection: ParametricosProjectionForm;
}

/** Construye la tabla de Proyección (ProyFin - CP), columnas Actual + Año 1..9, según SFI - ProyFin_CP.md. */
export const buildProjectionTable = ({
    hectares,
    yieldPerHectare,
    pricePerTon,
    costPerHectare,
    amortizationSchedule,
    longTermAmortizations,
    tasaContratada,
    creditoAutorizado,
    plazoCreditoMeses,
    projection,
}: BuildProjectionTableParams): ProjectionTable => {
    const grainIncomeYear1 = hectares * yieldPerHectare * pricePerTon;
    const productionCostYear1 = hectares * costPerHectare;

    const columnCount = projection.servicesIncome.length;
    const columns = (fn: (columnIndex: number) => number): Array<number> =>
        Array.from({ length: columnCount }, (_, columnIndex) => fn(columnIndex));

    const ingresosGrano = columns(() => grainIncomeYear1);
    const ingresosServicios = columns((i) => projection.servicesIncome[i] ?? 0);
    const ingresosApoyo = columns((i) => projection.supportIncome[i] ?? 0);
    const ventasNetas = ingresosGrano.map((value, i) => value + ingresosServicios[i] + ingresosApoyo[i]);

    const costosProduccion = columns(() => productionCostYear1);
    const costoRenta = columns((i) => projection.rentCost[i] ?? 0);
    const costosServicios = columns((i) => projection.servicesCost[i] ?? 0);
    const costosTotales = costosProduccion.map((value, i) => value + costoRenta[i] + costosServicios[i]);

    const utilidadOperativa = ventasNetas.map((value, i) => value - costosTotales[i]);

    const capitalAmortizacion = columns((i) => (i <= 3 ? (longTermAmortizations[i] ?? 0) : 0));
    const interesCreditoSimple = columns((i) => projection.interesCreditoSimple[i] ?? 0);
    const interesCapitalTrabajo = columns((i) => projection.workingCapitalInterest[i] ?? 0);
    const pagoObligaciones = capitalAmortizacion.map(
        (value, i) => value + interesCreditoSimple[i] + interesCapitalTrabajo[i],
    );

    const saldo1 = utilidadOperativa.map((value, i) => value - pagoObligaciones[i]);

    const capitalCredito = columns((i) => amortizationSchedule[i]?.capital ?? 0);
    const interesCredito = columns((i) => projection.interesCredito[i] ?? 0);
    const interesAvioCt = columns(() => (tasaContratada / 12) * creditoAutorizado * plazoCreditoMeses);
    const pagoCreditoProyectado = capitalCredito.map((value, i) => value + interesCredito[i] + interesAvioCt[i]);

    const saldo2 = saldo1.map((value, i) => value - pagoCreditoProyectado[i]);

    const otrosIngresos = columns((i) => projection.otherIncome[i] ?? 0);
    const otrosGastos = columns((i) => projection.otherExpenses[i] ?? 0);

    const utilidadEjercicio = saldo2.map((value, i) => value + otrosIngresos[i] - otrosGastos[i]);

    const capacidadDePago: Array<number | null> = utilidadOperativa.map((value, i) => {
        const numerator = value + (otrosIngresos[i] - otrosGastos[i]);
        const denominator = pagoObligaciones[i] + pagoCreditoProyectado[i];
        return denominator === 0 ? null : numerator / denominator;
    });

    const plazoCredito = columns(() => 12);

    return {
        ventasNetas,
        ingresosGrano,
        ingresosServicios,
        ingresosApoyo,
        costosTotales,
        costosProduccion,
        costoRenta,
        costosServicios,
        utilidadOperativa,
        pagoObligaciones,
        capitalAmortizacion,
        interesCreditoSimple,
        interesCapitalTrabajo,
        saldo1,
        pagoCreditoProyectado,
        capitalCredito,
        interesCredito,
        interesAvioCt,
        saldo2,
        otrosIngresos,
        otrosGastos,
        utilidadEjercicio,
        capacidadDePago,
        plazoCredito,
    };
};
